import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmt, fmtDate } from "../lib/utils";
import {
  Button, Input, Select, FormField, Dialog,
  Badge, Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui";
import { Plus, Package, Truck, CheckCircle, Wrench, Car } from "lucide-react";
import { toast } from "sonner";

// ─── Ciclo de vida del repuesto ────────────────────────────────────────────
// PRESUPUESTADO → PEDIDO → DISPONIBLE → USADO → ENTREGADO
// En cualquier punto → DADO_DE_BAJA

const ESTADO_META = {
  PRESUPUESTADO: { label: "Presupuestado", badge: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",    icon: Package },
  PEDIDO:        { label: "Pedido",         badge: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300", icon: Truck },
  DISPONIBLE:    { label: "En taller",      badge: "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle },
  USADO:         { label: "Instalado",      badge: "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300", icon: Wrench },
  ENTREGADO:     { label: "Entregado",      badge: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400",    icon: Car },
  DADO_DE_BAJA:  { label: "Baja",           badge: "bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400",           icon: null },
};

const TABS = [
  { key: "activos",    label: "Solicitados",  estados: ["PRESUPUESTADO", "PEDIDO"] },
  { key: "taller",     label: "En taller",    estados: ["DISPONIBLE"] },
  { key: "historial",  label: "Historial",    estados: ["USADO", "ENTREGADO", "DADO_DE_BAJA"] },
  { key: "catalogo",   label: "Catálogo",     estados: [] },
];

// Transiciones permitidas por estado actual
const TRANSICIONES = {
  PRESUPUESTADO: [{ a: "PEDIDO", label: "Marcar pedido", color: "bg-amber-100 text-amber-700 hover:bg-amber-200" }],
  PEDIDO:        [{ a: "DISPONIBLE", label: "Llegó al taller", color: "bg-green-100 text-green-700 hover:bg-green-200" }],
  DISPONIBLE:    [{ a: "USADO", label: "Instalado", color: "bg-purple-100 text-purple-700 hover:bg-purple-200" }],
  USADO:         [{ a: "ENTREGADO", label: "Entregado con auto", color: "bg-slate-100 text-slate-600 hover:bg-slate-200" }],
  ENTREGADO:     [],
  DADO_DE_BAJA:  [],
};

// ─── Componente principal ────────────────────────────────────────────────────
export default function Repuestos() {
  const [tab, setTab] = useState("activos");

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Repuestos" sub="Seguimiento por trabajo" />

      <div className="flex gap-0 mb-5 border-b border-border">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "catalogo" ? (
        <CatalogoTab />
      ) : (
        <RepuestosTab estados={TABS.find((t) => t.key === tab)?.estados ?? []} />
      )}
    </div>
  );
}

// ─── Tab: lista de presupuesto_repuestos filtrada por estados ────────────────
function RepuestosTab({ estados }) {
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [proveedores,setProv]       = useState([]);
  const [editRep,    setEditRep]    = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [bajaDialog, setBajaDialog] = useState(null);
  const [responsable,setResponsable]= useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = estados.map((e) =>
        api.get("/presupuesto-repuestos", { params: { estado: e } })
      );
      // Si no hay estados (todos), traer sin filtro
      const results = estados.length
        ? await Promise.all(requests)
        : [await api.get("/presupuesto-repuestos")];
      const merged = results.flatMap((r) => r.data);
      // Ordenar: más recientes primero
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setList(merged);
    } finally {
      setLoading(false);
    }
  }, [estados]);

  useEffect(() => {
    api.get("/proveedores").then((r) => setProv(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  function openEdit(rep) {
    setEditRep(rep);
    setEditForm({
      proveedor_id:           rep.proveedor_id ?? "",
      fecha_estimada_llegada: rep.fecha_estimada_llegada ?? "",
      notas:                  rep.notas ?? "",
      descripcion:            rep.descripcion ?? "",
      precio_costo:           rep.precio_costo ?? 0,
      cantidad:               rep.cantidad ?? 1,
    });
  }

  async function saveEdit(e) {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`/presupuesto-repuestos/${editRep.id}`, {
        proveedor_id:           editForm.proveedor_id ? parseInt(editForm.proveedor_id) : null,
        fecha_estimada_llegada: editForm.fecha_estimada_llegada || null,
        notas:                  editForm.notas || null,
        descripcion:            editForm.descripcion || null,
        precio_costo:           parseFloat(editForm.precio_costo) || 0,
        cantidad:               parseFloat(editForm.cantidad) || 1,
      });
      toast.success("Actualizado");
      setEditRep(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally { setSavingEdit(false); }
  }

  async function cambiarEstado(id, nuevoEstado, resp) {
    try {
      await api.patch(`/presupuesto-repuestos/${id}/estado`, null, {
        params: { estado: nuevoEstado, responsable: resp || undefined },
      });
      toast.success(`${ESTADO_META[nuevoEstado]?.label ?? nuevoEstado}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    }
  }

  const filtered = list.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.descripcion?.toLowerCase().includes(q) ||
      r.codigo?.toLowerCase().includes(q) ||
      r.presupuesto_numero?.toLowerCase().includes(q) ||
      r.cliente_nombre?.toLowerCase().includes(q) ||
      r.vehiculo_desc?.toLowerCase().includes(q)
    );
  });

  const totalGanancia = filtered.reduce((sum, r) => {
    const presup = parseFloat(r.precio_presupuestado) || 0;
    const costo  = parseFloat(r.precio_costo) || 0;
    const qty    = parseFloat(r.cantidad) || 1;
    return sum + (presup - costo) * qty;
  }, 0);

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por repuesto, cliente, vehículo, presupuesto…"
          className="w-full sm:w-80"
        />
        <span className="text-sm text-muted-foreground self-center">{filtered.length} item(s)</span>
        {filtered.some((r) => parseFloat(r.precio_presupuestado) > 0) && (
          <span className={`text-sm font-medium self-center ml-auto ${totalGanancia >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
            Ganancia total: {totalGanancia >= 0 ? "+" : ""}{fmt(totalGanancia)}
          </span>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Presupuesto</TableHead>
            <TableHead>Repuesto</TableHead>
            <TableHead>Cliente / Vehículo</TableHead>
            <TableHead className="text-right">Cant.</TableHead>
            <TableHead className="text-right">P. Presup.</TableHead>
            <TableHead className="text-right">P. Costo</TableHead>
            <TableHead className="text-right">Ganancia</TableHead>
            <TableHead>Proveedor</TableHead>
            <TableHead>F. Est. llegada</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={11}><Empty message="Sin repuestos en esta etapa" icon={Package} /></TableCell>
            </TableRow>
          )}
          {filtered.map((rep) => {
            const meta = ESTADO_META[rep.estado];
            const siguientes = TRANSICIONES[rep.estado] ?? [];
            return (
              <TableRow key={rep.id}>
                <TableCell className="font-mono text-xs">{rep.presupuesto_numero}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium">{rep.descripcion}</div>
                  {rep.codigo && <div className="text-xs text-muted-foreground font-mono">{rep.codigo}</div>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  <div className="font-medium text-foreground">{rep.cliente_nombre}</div>
                  <div>{rep.vehiculo_desc}</div>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{rep.cantidad}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {rep.precio_presupuestado > 0 ? fmt(rep.precio_presupuestado) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{fmt(rep.precio_costo)}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {(() => {
                    const presup = parseFloat(rep.precio_presupuestado) || 0;
                    const costo  = parseFloat(rep.precio_costo) || 0;
                    const qty    = parseFloat(rep.cantidad) || 1;
                    if (presup === 0) return <span className="text-muted-foreground">—</span>;
                    const g = (presup - costo) * qty;
                    return (
                      <span className={g >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                        {g >= 0 ? "+" : ""}{fmt(g)}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-sm">{rep.proveedor_nombre ?? "—"}</TableCell>
                <TableCell className="text-xs">
                  {rep.fecha_estimada_llegada ? fmtDate(rep.fecha_estimada_llegada) : "—"}
                </TableCell>
                <TableCell>
                  <Badge className={`${meta?.badge ?? "bg-secondary"} text-xs`}>
                    {meta?.label ?? rep.estado}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 min-w-[140px]">
                    {siguientes.map((t) => (
                      <button
                        key={t.a}
                        onClick={() => cambiarEstado(rep.id, t.a)}
                        className={`text-xs px-2 py-0.5 rounded text-left ${t.color}`}
                      >
                        → {t.label}
                      </button>
                    ))}
                    {rep.estado !== "DADO_DE_BAJA" && rep.estado !== "ENTREGADO" && (
                      <button
                        onClick={() => { setBajaDialog(rep.id); setResponsable(""); }}
                        className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100 text-left"
                      >
                        Dar de baja
                      </button>
                    )}
                    {rep.estado !== "ENTREGADO" && rep.estado !== "DADO_DE_BAJA" && (
                      <button
                        onClick={() => openEdit(rep)}
                        className="text-xs px-2 py-0.5 rounded bg-secondary hover:bg-border text-left"
                      >
                        Editar
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Dialog editar */}
      <Dialog open={!!editRep} onClose={() => setEditRep(null)} title="Editar repuesto" size="sm">
        <form onSubmit={saveEdit} className="space-y-3">
          <FormField label="Descripción">
            <Input
              value={editForm.descripcion}
              onChange={(e) => setEditForm((f) => ({ ...f, descripcion: e.target.value }))}
              required
            />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Cantidad">
              <Input
                type="number" min="0.01" step="0.01"
                value={editForm.cantidad}
                onChange={(e) => setEditForm((f) => ({ ...f, cantidad: e.target.value }))}
              />
            </FormField>
            <FormField label="Precio costo">
              <Input
                type="number" min="0" step="100"
                value={editForm.precio_costo}
                onChange={(e) => setEditForm((f) => ({ ...f, precio_costo: e.target.value }))}
              />
            </FormField>
          </div>
          <FormField label="Proveedor">
            <Select
              value={editForm.proveedor_id}
              onChange={(e) => setEditForm((f) => ({ ...f, proveedor_id: e.target.value }))}
            >
              <option value="">Sin proveedor</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Select>
          </FormField>
          <FormField label="Fecha estimada llegada">
            <Input
              type="date"
              value={editForm.fecha_estimada_llegada}
              onChange={(e) => setEditForm((f) => ({ ...f, fecha_estimada_llegada: e.target.value }))}
            />
          </FormField>
          <FormField label="Notas">
            <Input
              value={editForm.notas}
              onChange={(e) => setEditForm((f) => ({ ...f, notas: e.target.value }))}
              placeholder="Observaciones…"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setEditRep(null)}>Cancelar</Button>
            <Button type="submit" loading={savingEdit}>Guardar</Button>
          </div>
        </form>
      </Dialog>

      {/* Dialog dar de baja */}
      <Dialog open={!!bajaDialog} onClose={() => setBajaDialog(null)} title="Dar de baja" size="sm">
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Indicá quién autoriza la baja de este repuesto.</p>
          <FormField label="Responsable *">
            <Input
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder="Nombre del responsable"
              required
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBajaDialog(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={!responsable.trim()}
              onClick={() => { cambiarEstado(bajaDialog, "DADO_DE_BAJA", responsable); setBajaDialog(null); }}
            >
              Confirmar baja
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Tab: Catálogo de piezas conocidas (referencia, no inventario) ────────────
function CatalogoTab() {
  const [list,       setList]       = useState([]);
  const [proveedores,setProv]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [open,       setOpen]       = useState(false);
  const [editItem,   setEdit]       = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [form,       setForm]       = useState(emptyForm());

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([
      api.get("/repuestos", { params: { q: search || undefined } }),
      api.get("/proveedores"),
    ]);
    setList(r.data);
    setProv(p.data);
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openEdit(item) {
    setEdit(item);
    setForm({
      codigo:      item.codigo ?? "",
      nombre:      item.nombre,
      categoria:   item.categoria ?? "",
      precio_costo: item.precio_costo,
      proveedor_id: item.proveedor_id ?? "",
      unidad:      item.unidad,
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      stock_actual: 0,
      stock_minimo: 0,
      precio_venta: 0,
      proveedor_id: form.proveedor_id || null,
      codigo:       form.codigo || null,
      categoria:    form.categoria || null,
    };
    try {
      if (editItem) {
        await api.put(`/repuestos/${editItem.id}`, body);
        toast.success("Repuesto actualizado");
      } else {
        await api.post("/repuestos", body);
        toast.success("Repuesto creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally { setSaving(false); }
  }

  const filtered = list.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.nombre?.toLowerCase().includes(q) || r.codigo?.toLowerCase()?.includes(q);
  });

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o código…" className="w-64" />
        <Button onClick={() => { setEdit(null); setForm(emptyForm()); setOpen(true); }} className="ml-auto">
          <Plus className="w-4 h-4" /> Nuevo
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Categoría</TableHead>
            <TableHead className="text-right">P. Referencia</TableHead>
            <TableHead>Proveedor</TableHead>
            <TableHead>Unidad</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={7}><Empty message="Sin repuestos en el catálogo" icon={Package} /></TableCell></TableRow>
          )}
          {filtered.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs">{r.codigo ?? "—"}</TableCell>
              <TableCell className="text-sm">{r.nombre}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.categoria ?? "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(r.precio_costo)}</TableCell>
              <TableCell className="text-sm">{r.proveedor_nombre ?? "—"}</TableCell>
              <TableCell className="text-xs">{r.unidad}</TableCell>
              <TableCell>
                <button
                  onClick={() => openEdit(r)}
                  className="text-xs px-2 py-1 rounded bg-secondary hover:bg-border transition-colors"
                >
                  Editar
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} title={editItem ? "Editar pieza" : "Nueva pieza en catálogo"} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} required />
            </FormField>
            <FormField label="Código">
              <Input value={form.codigo} onChange={(e) => set("codigo", e.target.value)} placeholder="Ej: FAR-001" />
            </FormField>
            <FormField label="Categoría">
              <Input value={form.categoria} onChange={(e) => set("categoria", e.target.value)} placeholder="Ej: Carrocería, Pintura…" />
            </FormField>
            <FormField label="Precio referencia (costo)">
              <Input type="number" min="0" step="100" value={form.precio_costo} onChange={(e) => set("precio_costo", parseFloat(e.target.value) || 0)} />
            </FormField>
            <FormField label="Unidad">
              <Input value={form.unidad} onChange={(e) => set("unidad", e.target.value)} />
            </FormField>
            <FormField label="Proveedor habitual" className="col-span-full">
              <Select value={form.proveedor_id} onChange={(e) => set("proveedor_id", e.target.value)}>
                <option value="">Sin proveedor</option>
                {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </Select>
            </FormField>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

function emptyForm() {
  return { codigo: "", nombre: "", categoria: "", precio_costo: 0, proveedor_id: "", unidad: "UND" };
}
